import JSON from "json5";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { sharedEndpoints } from "@src/services/endpoints";
import {
  IMPERSONATED_SESSION_COOKIE,
  SESSION_ID_COOKIE,
  USER_COOKIE,
} from "../constants/constants";
import packageJson from "../../package.json" with { type: "json" };
import StorageUtils from "./storageUtils";
import AppUtils from "./appUtils";

// ----------------------------------------
// Initialize page view tracking

let trackingAuthContext = null;

/** Activities with a specific actionKey */
export const trackActionActivity = (
  actionKey,
  eventData = {},
  forceTracking = false,
) => {
  console.log("trackActionActivity", actionKey, eventData);
  console.log(
    "shouldTrackClicks",
    shouldTrackClicks,
    "forceTracking",
    forceTracking,
  );
  let action = "PAGE_ACTION";
  if (String(actionKey).toUpperCase().includes("FEEDBACK")) {
    action = "FEEDBACK";
  }
  if (!shouldTrackClicks && !forceTracking) {
    return;
  }
  try {
    trackActivityBase(action, {
      ...(eventData || {}),
      actionKey,
    });
  } catch (e) {
    console.warn("trackActionActivity error", e);
  }
};

export function trackError(error, actionKey, additionalData = {}) {
  try {
    console.debug("track error", error);
    trackActivityBase(
      "ERROR",
      AppUtils.simplifyError(error, {
        ...additionalData,
        actionKey: actionKey || additionalData?.actionKey || error?.name || "",
      }),
    );
  } catch (e) {
    console.error("trackError errored", e);
  }
}
export function trackLoginError(error, actionKey, additionalData = {}) {
  try {
    trackActivityBase(
      "LOGIN_ERROR",
      AppUtils.simplifyError(error, {
        ...additionalData,
        actionKey: actionKey || additionalData?.actionKey || error?.name || "",
      }),
    );
  } catch (e) {
    console.error("trackLoginError errored", e);
  }
}

const trackActivityBase = (name, eventData) => {
  try {
    const trackingInfo = makePayload(name, eventData);
    sendTrackingData(trackingInfo);
  } catch (e) {
    console.warn("trackActivity error", e);
  }
};

export const trackSession = (authData) => {
  if (authData != null) {
    //Custom payload from authData because authContext likely doesn't exist yet
    if (!!authData?.isSuperAdmin) return;
    if (!shouldTrackPageView) return;

    StorageUtils.setItem(SESSION_ID_COOKIE, uuidv4());

    const { customerCode, username, roles } = authData;
    const activeRole = window.location.pathname.includes("operator")
      ? "CAPTAIN"
      : window.location.pathname.includes("manager")
        ? "FUEL_MANAGER"
        : roles?.[0] || "unknown";
    const payload = makePayload(
      "SESSION",
      {},
      customerCode,
      username,
      activeRole,
    );
    sendTrackingData(payload);
    hotjarIdentifyUser();
  } else if (trackingAuthContext != null) {
    StorageUtils.setItem(SESSION_ID_COOKIE, uuidv4());
    trackActivityBase("SESSION", trackingAuthContext);
    hotjarIdentifyUser();
  }
};

const trackClickActivity = (event) => {
  if (!shouldTrackClicks) return;
  const trackableEl = event.target.closest("[data-track]");

  if (trackableEl) {
    const trackedData = trackableEl.getAttribute("data-track");
    if (!trackedData) return;

    trackActivityBase("PAGE_ACTION", JSON.parse(trackedData));
  } else console.debug?.("trackClickActivity skipped, no trackedData", event);
};

let CLICK_TRACKING_INITIALIZED = false;
let shouldTrackClicks = false;

export function initUserActivityTracker(authContext, shouldTrack = true) {
  // Initialize user activity tracking only once
  if (CLICK_TRACKING_INITIALIZED) return;
  CLICK_TRACKING_INITIALIZED = true;
  shouldTrackClicks = shouldTrack;
  console.log("initUserActivityTracker", shouldTrackClicks, authContext);

  // Store the auth context for later use
  if (authContext) {
    trackingAuthContext = authContext;
  }

  // Attach click event listener to the document
  document.addEventListener("click", trackClickActivity);
}

export function stopUserActivityTracker() {
  // Remove the click event listener when no longer needed
  if (!CLICK_TRACKING_INITIALIZED) return;
  CLICK_TRACKING_INITIALIZED = false;
  document.removeEventListener("click", trackClickActivity);
}

// ----------------------------------------

// ----------------------------------------
// Initialize page view tracking

let PAGE_VIEW_TRACKING_INITIALIZED = false;
let shouldTrackPageView = true;

export function usePageViewTracker(authContext, shouldTrack = true) {
  const location = useLocation();

  useEffect(() => {
    // Store the auth context for later use
    if (authContext) {
      trackingAuthContext = authContext;
    }
  }, [authContext]);

  // Ensure this effect runs only once
  useEffect(() => {
    if (PAGE_VIEW_TRACKING_INITIALIZED) return;
    PAGE_VIEW_TRACKING_INITIALIZED = true;
    shouldTrackPageView = shouldTrack;
  }, [shouldTrack]);

  // Track page view on location change
  useEffect(() => {
    if (!shouldTrackPageView) return;
    const payload = makePayload("PAGE_VIEW", {
      path: location.pathname + location.search,
    });

    sendTrackingData(payload);
  }, [location]);
}

export function stopPageViewTracker() {
  shouldTrackPageView = false;
}

// ----------------------------------------

const makePayload = (name, attr, customerCode, username, activeRole) => {
  customerCode =
    customerCode ?? trackingAuthContext?.user.customerCode ?? "unknown";
  username = username ?? trackingAuthContext?.user.username ?? "unknown";
  activeRole = activeRole ?? trackingAuthContext?.activeRole ?? "unknown";
  const isError = String(name).toUpperCase().includes("ERROR");
  const isFeedback = String(name).toUpperCase().includes("FEEDBACK");
  if (
    !isError &&
    !isFeedback &&
    username === "unknown" &&
    customerCode === "unknown" &&
    attr?.actionKey == null
  ) {
    //Ignore anonymous/unauthenticated APP_EVENTs unless they have an actionKey
    return {};
  }
  if (
    attr.status === 401 ||
    (attr.error?.message ?? attr.error) === "Unauthorized"
  ) {
    //Ignore tracking simple "401 Unauthorized" errors
    return {};
  }
  const type = isError ? "OTHER" : "APP_EVENT";
  let scope = `${customerCode}:${activeRole}`;
  if (isFeedback) {
    scope = "FEEDBACK:CAPTAIN";
  } else if (!isFeedback && scope.includes("unknown")) {
    if (isError) {
      scope = "ERRORS";
    } else {
      //APP_EVENT requires a valid scope
      return {};
    }
  }

  const { actionKey, ...rest } = attr ?? {};
  return {
    type,
    name,
    username,
    scope,
    data: {
      sessionId: StorageUtils.getItem(SESSION_ID_COOKIE) || null,
      timestamp: new Date().getTime(),
      helpModeEnabled: null,
      url: location.pathname + location.search,
      attr: rest || {},
      actionKey: actionKey || attr.key || "",
      userAgent: window?.navigator?.userAgent,
      usingPWA: "standalone" in window.navigator && window.navigator.standalone,
      appVersion: `${packageJson.name}@${packageJson.version}`,
    },
  };
};

let lastTrackingRequestPayload = null;
const isSameTrackingRequest = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  const cleanA = { ...a, data: { ...(a.data || {}) } };
  const cleanB = { ...b, data: { ...(b.data || {}) } };
  if (cleanA.data) delete cleanA.data.timestamp;
  if (cleanB.data) delete cleanB.data.timestamp;
  return JSON.stringify(cleanA) === JSON.stringify(cleanB);
};
const sendTrackingData = (payload) => {
  if (Object.keys(payload || {}).length === 0) {
    return;
  }
  if (isSameTrackingRequest(payload, lastTrackingRequestPayload)) {
    return;
  } else lastTrackingRequestPayload = payload;

  const isImpersonating =
    Object.keys(StorageUtils.getObjectItem(IMPERSONATED_SESSION_COOKIE) || {})
      .length > 0;
  const shouldSendToBackend = !isImpersonating;

  //Log with eyes emoji 👀
  console.log(
    `\u{1F440}${shouldSendToBackend ? "" : "(Skip)"}`,
    `${payload.name} ${payload.data?.actionKey} {${Object.keys(payload.data?.attr || {}).join()}}`,
    payload,
  );

  const hotjarEventName = [payload.name, payload.data.actionKey]
    .filter(Boolean)
    .join("__");
  hotjarTriggerEvent(hotjarEventName);

  if (!shouldSendToBackend) {
    return;
  }

  sharedEndpoints.trackActivity(payload);
};

/** https://help.hotjar.com/hc/en-us/articles/4405109971095-Events-API-Reference */
const hotjarTriggerEvent = (eventName) => {
  try {
    window.hj("event", eventName);
  } catch (e) {
    console.warn("failed sending hotjar event: ", e);
  }
};

/** Give Hotjar current user info (using localstorage). */
export const hotjarIdentifyUser = () => {
  try {
    //This should mostly match code in /index.html <script /> tag
    const userCookie = StorageUtils.getObjectItem(USER_COOKIE);
    const userId = userCookie?.identityId ?? userCookie?.id;
    if (userId != null) {
      const adminSessionCookie = StorageUtils.getObjectItem(
        IMPERSONATED_SESSION_COOKIE,
      );
      window.hj("identify", userId, {
        id: userCookie.id,
        username: userCookie.username || userCookie.email,
        customer: userCookie.customerCode,
        industry:
          userCookie.customer?.industry || userCookie?.metadata?.["industry"],
        auth_roles: (userCookie.roles || []).join(",") || "",
        role_alias: userCookie.metadata?.["role_alias"] || "",
        operator_code: userCookie.metadata?.["external_id"] || "",
        name: `${userCookie.firstname || ""} ${userCookie.lastname || ""}`,
        createdAt: userCookie.createdAt,
        impersonated: adminSessionCookie?.admin?.id != null,
        usingPWA:
          "standalone" in window.navigator && window.navigator.standalone,
        sessionId: StorageUtils.getItem(SESSION_ID_COOKIE),
        timezone:
          window?.Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
      });
    } else {
      //identify as not authenticated
      window.hj("identify", null);
    }
  } catch (e) {
    console.warn("Failed to identify user to hotjar ", e);
  }
};

/** Activity tracking for returning to the app */
export function installGlobalRevisitTracking() {
  try {
    let lastHiddenTime = null;
    let lastReturnLogged = null;

    const MIN_AWAY_MS = 15 * 60 * 1000; // 15 minutes
    const RETURN_DEBOUNCE_MS = 500; // Avoid double-firing within 500ms

    function logReturnEvent() {
      if (!shouldTrackPageView || !lastHiddenTime) return;
      const now = Date.now();
      // Avoid double firing events
      if (lastReturnLogged && now - lastReturnLogged < RETURN_DEBOUNCE_MS)
        return;

      const durationAwayMs = now - lastHiddenTime;
      if (durationAwayMs >= MIN_AWAY_MS) {
        lastReturnLogged = now;
        trackActivityBase("REFOCUS", {
          durationAwaySeconds: Math.floor(durationAwayMs / 1000),
        });
      }
      lastHiddenTime = null;
    }

    document.addEventListener("visibilitychange", () => {
      console.debug?.("visibilitychange", document.visibilityState);

      if (document.visibilityState === "hidden") {
        if (lastHiddenTime === null) {
          lastHiddenTime = Date.now();
        }
      } else {
        //visible
        if (lastHiddenTime != null) {
          logReturnEvent();
        }
      }
    });
  } catch (e) {
    console.error("Failed to add visibilitychange tracking", e);
  }
}

export function installGlobalEventListeners() {
  try {
    // Global Error
    window.addEventListener("error", (event) => {
      if (event.error) {
        trackError(event.error);
      }
    });

    // Global Promise rejection
    window.addEventListener("unhandledrejection", (event) => {
      trackError({
        name: "Error: unhandledrejection",
        message: event.reason,
      });
    });
  } catch (e) {
    console?.error?.("Failed to add global event listeners ", e);
  }
}
